import humps from 'humps';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type {
  ActionMetadata,
  Brand,
  Identity,
  PermissionObject,
  RawBrand,
  RawIdentity,
  Subscriptions,
} from '@/types/auth.types';

import {
  BOARD_LIMIT_PERMISSIONS,
  type BoardType,
  guard as permissionsGuard,
  guardAll,
  SCHEDULER_AUTO_PUBLISH_PERMISSIONS,
  SCHEDULER_SEND_NOTIFICATION_PERMISSIONS,
} from '@/models/auth/permissions.enum';
import {
  browserStorageGetItem,
  browserStorageRemoveItem,
  browserStorageSetItem,
} from '@/utils/browserStorage';

const INTERNAL_EMAIL_DOMAIN = '@dashsocial.com';

export const useIdentityStore = defineStore('identity', () => {
  const identity = ref<Identity | null>(null);
  const subscriptions = ref<Subscriptions | null>(null);
  const actionMetadata = ref<ActionMetadata | null>(null);
  const currentBrandLabel = ref(browserStorageGetItem('currentBrand'));

  const canOnlyAccessInactiveBrand = computed<boolean>(
    () =>
      (identity.value?.accessible_brands ?? []).length === 0 &&
      Object.keys(identity.value?.inactive_brands ?? {}).length === 1,
  );

  const currentBrand = computed<Brand | null>(() => {
    const brandKey = currentBrandLabel.value;
    if (!brandKey) return null;
    if (identity.value?.brands?.[brandKey]) {
      return identity.value.brands[brandKey];
    }
    if (canOnlyAccessInactiveBrand.value) {
      return identity.value?.inactive_brands?.[brandKey] ?? null;
    }
    return null;
  });

  const allUserBrands = computed<Brand[]>(() => {
    const identityBrands = identity.value?.brands || {};
    return Object.values(identityBrands);
  });

  const allActiveUserBrands = computed<Brand[]>(() =>
    (identity.value?.accessible_brands || [])
      .map((brandId) => allUserBrands.value.find((brand) => brand.id === brandId))
      .filter((brand): brand is Brand => !!brand?.isActive),
  );

  const currentBrandSubscriptions = computed<unknown[] | null>(() => {
    if (!currentBrand.value) {
      return null;
    }
    return subscriptions.value?.brand?.[currentBrand.value.id] ?? null;
  });

  const currentOrganizationSubscriptions = computed<unknown[] | null>(() => {
    if (!currentBrand.value?.organization?.id) {
      return null;
    }
    return subscriptions.value?.organization?.[currentBrand.value.organization.id] ?? null;
  });

  const currentSubscriptions = computed<unknown[] | null>(() => {
    if (!currentBrand.value) {
      return null;
    }
    return [
      ...(currentBrandSubscriptions.value || []),
      ...(currentOrganizationSubscriptions.value || []),
    ];
  });

  const brand_can = computed(
    () =>
      (module: string, permission: string, brand: Brand | null = null): boolean | number => {
        const resolvedBrand = brand || currentBrand.value;
        return resolvedBrand?.permissions?.[module]
          ? resolvedBrand.permissions[module][permission]
          : false;
      },
  );

  const user_can = computed(
    () =>
      (
        module: string,
        permission: string,
        brand: Brand | null = null,
      ): boolean | null | undefined => {
        const resolvedBrand = brand || currentBrand.value;
        return (
          resolvedBrand &&
          identity.value?.permissions?.[module]?.[resolvedBrand.label]?.[permission]
        );
      },
  );

  const identityBrandsById = computed<Record<number, Brand>>(() =>
    Object.keys(identity.value?.brands ?? {}).reduce<Record<number, Brand>>((acc, brandLabel) => {
      const brand = identity.value!.brands![brandLabel];
      if (brand?.id) {
        acc[brand.id] = brand;
      }
      return acc;
    }, {}),
  );

  const isSuperAdmin = computed<boolean>(() => !!identity.value?.is_superadmin);

  const isShared = computed<boolean>(() => !!identity.value?.is_shared);

  /** Employee, by email domain. Internal tooling only — never gate data on it. */
  const isDashSocialUser = computed<boolean>(
    () => identity.value?.email?.endsWith(INTERNAL_EMAIL_DOMAIN) ?? false,
  );

  const canAccessOrganic = computed<boolean | null | undefined>(
    () =>
      user_can.value('scheduler', 'can_access_scheduler') ||
      user_can.value('pinterest', 'can_access_pint') ||
      user_can.value('instagram', 'can_access_instagram'),
  );

  const canAccessVision = computed<boolean | number>(
    () =>
      brand_can.value('vision', 'can_access_vision') &&
      !!user_can.value('vision', 'can_access_vision'),
  );

  const canAccessStoriesSwipeups = computed<boolean | null | undefined>(() =>
    user_can.value('instagram', 'can_access_stories_swipeup'),
  );

  const brandSchedulerPermissionAllows = (permission: string, brand: Brand | null): boolean => {
    const resolvedBrand = brand ?? currentBrand.value;
    // Missing keys count as granted so cached brand payloads fail open while new permissions roll out
    return resolvedBrand?.permissions?.scheduler?.[permission] !== false;
  };

  const brandCanAccessScheduler = computed(
    () =>
      (platform: string, brand: Brand | null = currentBrand.value): boolean =>
        brandSchedulerPermissionAllows(`can_access_scheduler_${platform}`, brand),
  );

  const canAccessSchedulerPlatform = computed(
    () =>
      (platform: string, brand: Brand | null = currentBrand.value): boolean | undefined => {
        const platformMap: Record<string, string> = {
          instagram: 'ig',
          pinterest: 'pint',
          twitter: 'twit',
          facebook: 'fb',
          tiktok: 'tiktok',
          linkedin: 'linkedin',
        };
        const name = platformMap[platform] ?? platform;
        return (
          !!brandCanAccessScheduler.value(platform, brand) &&
          !!user_can.value('scheduler', 'can_access_scheduler', brand) &&
          !!user_can.value('scheduler', `can_access_scheduler_${name}`, brand)
        );
      },
  );

  const canAccessAutoPublish = computed(
    () =>
      (platform: string, brand: Brand | null = currentBrand.value): boolean => {
        const permission = SCHEDULER_AUTO_PUBLISH_PERMISSIONS[platform];
        return (
          !!permission &&
          !!canAccessSchedulerPlatform.value(platform, brand) &&
          brandSchedulerPermissionAllows(permission, brand) &&
          !!user_can.value('scheduler', permission, brand)
        );
      },
  );

  const canAccessSendNotification = computed(
    () =>
      (platform: string, brand: Brand | null = currentBrand.value): boolean => {
        const permission = SCHEDULER_SEND_NOTIFICATION_PERMISSIONS[platform];
        return (
          !!permission &&
          !!canAccessSchedulerPlatform.value(platform, brand) &&
          brandSchedulerPermissionAllows(permission, brand)
        );
      },
  );

  const isSchedulerPlatformUsable = computed(
    () =>
      (platform: string, brand: Brand | null = currentBrand.value): boolean => {
        if (!canAccessSchedulerPlatform.value(platform, brand)) return false;
        const autoPublishPermission = SCHEDULER_AUTO_PUBLISH_PERMISSIONS[platform];
        const sendNotificationPermission = SCHEDULER_SEND_NOTIFICATION_PERMISSIONS[platform];
        // Visibility is driven by brand-level publish permissions only; user-level
        // auto-publish permission gates the publish option, not the platform
        return (
          (!!autoPublishPermission &&
            brandSchedulerPermissionAllows(autoPublishPermission, brand)) ||
          (!!sendNotificationPermission &&
            brandSchedulerPermissionAllows(sendNotificationPermission, brand))
        );
      },
  );

  const brandCanAccessIndustryBenchmarks = computed<boolean | number>(() =>
    brand_can.value('dashboard', 'can_access_industry_benchmarks'),
  );

  const hasBoardsAccess = computed(
    () =>
      (boardType: BoardType, customBrand?: Brand | null): boolean => {
        const brand = customBrand ?? currentBrand.value;
        const boardPermission = BOARD_LIMIT_PERMISSIONS[boardType];
        if (!boardPermission || !brand) return false;
        const { module, permission } = boardPermission;
        const value =
          brand && identity.value?.brands?.[brand.label]?.permissions?.[module]?.[permission];
        return typeof value === 'number' && value > 0;
      },
  );

  const currentBrandRole = computed<string | undefined>(() => {
    const brandRoles = identity.value?.permissions?.brand_roles ?? [];
    return brandRoles.find((brandRole) => brandRole.brand_id === currentBrand.value?.id)?.role;
  });

  const currentPlanType = computed<string | undefined>(() => currentBrand.value?.plan?.plan_type);

  const isCurrentBrandRoleAdmin = computed<boolean>(() => currentBrandRole.value === 'admin');

  const identityBrandIds = computed<number[]>(() => {
    if (Object.keys(identity.value?.brands ?? {}).length > 0) {
      return Object.keys(identity.value!.brands!).map(
        (brandLabel) => identity.value!.brands![brandLabel].id,
      );
    }
    if (canOnlyAccessInactiveBrand.value && currentBrand.value) {
      return [currentBrand.value.id];
    }
    return [];
  });

  const canAccessMultipleBrands = computed<boolean>(
    () => (identity.value?.accessible_brands ?? []).length > 1,
  );

  /**
   * Transforms raw brand objects (snake_case from API) to Brand objects (camelCase).
   * Used for both active brands and inactive_brands.
   */
  function extractBrands(rawBrands: Record<string, RawBrand> | undefined): Record<string, Brand> {
    return Object.keys(rawBrands ?? {}).reduce<Record<string, Brand>>((brandsMap, key) => {
      const rawBrand: RawBrand = rawBrands![key];
      const {
        avatar_url: avatarUrl,
        created_at: createdAt,
        id,
        label,
        name,
        organization_id: organizationId,
        organization,
        permissions,
        plan,
        stripe_subscription_id: stripeSubscriptionId,
        stripe_subscription_status: stripeSubscriptionStatus,
        stripe_subscription_billing_interval: stripeSubscriptionBillingInterval,
        stripe_subscription_current_period_end: stripeSubscriptionCurrentPeriodEnd,
        is_active: isActive,
        kawo_brand_id: kawoBrandId,
        tags,
      } = rawBrand;
      return {
        ...brandsMap,
        [key]: {
          avatarUrl,
          createdAt,
          id,
          label,
          name,
          organizationId,
          organization,
          permissions,
          plan,
          stripeSubscriptionId,
          stripeSubscriptionStatus,
          stripeSubscriptionBillingInterval,
          stripeSubscriptionCurrentPeriodEnd,
          isActive,
          kawoBrandId,
          tags: tags?.map((tag) => humps.camelizeKeys(tag)).reverse(),
        } as Brand,
      };
    }, {});
  }

  function guard(
    permissionObject: PermissionObject | PermissionObject[],
    customBrand: Brand | null = null,
  ): boolean {
    const brand = customBrand ?? currentBrand.value;
    return Array.isArray(permissionObject)
      ? guardAll(identity.value, brand, permissionObject)
      : permissionsGuard(identity.value, brand, permissionObject);
  }

  function guardSome(
    permissionObjects: PermissionObject[],
    targetBrand: Brand | null = null,
  ): boolean {
    const brand = targetBrand ?? currentBrand.value;
    return permissionObjects.some((permissionObject) => guard(permissionObject, brand));
  }

  function gtmTrack(): void {
    window.dataLayer?.push({
      event: 'Brand Change',
      location: identity.value?.time_zone_name,
      brandId: currentBrand.value?.id,
      brandName: currentBrandLabel.value,
      orgId: identity.value?.organization_id,
      orgName: identity.value?.organization?.name,
      jobTitle: identity.value?.job_title,
      plan: currentPlanType.value,
      userId: identity.value?.id,
      userEmail: identity.value?.email,
    });
  }

  function setCurrentBrand({ brandLabel }: { brandLabel: string }): void {
    browserStorageSetItem('currentBrand', brandLabel);
    currentBrandLabel.value = brandLabel;
    gtmTrack();
  }

  function clearCurrentBrand(): void {
    browserStorageRemoveItem('currentBrand');
    currentBrandLabel.value = null;
  }

  function setIdentity({ identity: newIdentity }: { identity: RawIdentity }): void {
    if (!newIdentity) {
      return;
    }

    const identityBrandLabels = Object.keys(newIdentity?.brands ?? {});
    const inactiveBrandLabels = Object.keys(newIdentity?.inactive_brands ?? {});

    // Transform brands and inactive_brands from snake_case (RawBrand) to camelCase (Brand)
    const transformedBrands = extractBrands(newIdentity?.brands);
    const transformedInactiveBrands = extractBrands(newIdentity?.inactive_brands);

    // Spread raw identity so new API fields (e.g. created_at) flow through automatically,
    // then override brands/inactive_brands with camelCase-transformed versions
    identity.value = {
      ...newIdentity,
      brands: transformedBrands,
      inactive_brands: transformedInactiveBrands,
    };

    // Initialize current brand to first brand in user's list of accessible brands (priority) or
    // inactive brands if there isn't one already selected.
    if (canOnlyAccessInactiveBrand.value) {
      setCurrentBrand({ brandLabel: inactiveBrandLabels[0] });
    } else if (
      (!currentBrandLabel.value || !identityBrandLabels.includes(currentBrandLabel.value)) &&
      identityBrandLabels.length > 0
    ) {
      setCurrentBrand({ brandLabel: identityBrandLabels[0] });
    }
  }

  function setSubscriptions(payload: Subscriptions | null): void {
    if (payload) {
      subscriptions.value = payload;
    }
  }

  function setActionMetadata(payload: ActionMetadata | null): void {
    if (payload) {
      actionMetadata.value = payload;
    }
  }

  function updateIdentity({ data }: { data: Record<string, unknown> }): void {
    const snakeCaseData = humps.decamelizeKeys(data) as Record<string, unknown>;
    Object.keys(snakeCaseData).forEach((key) => {
      if (identity.value) {
        (identity.value as Record<string, unknown>)[key] = snakeCaseData[key];
      }
    });
  }

  function clearIdentity(): void {
    identity.value = null;
  }

  async function setCurrentBrandByAttributes({ brand }: { brand: Brand }): Promise<void> {
    identity.value = {
      ...identity.value,
      brands: {
        'Token User': brand,
      },
    } as Identity;
    currentBrandLabel.value = 'Token User';
  }

  function checkIfBrandPermissionsExist(
    permissions: PermissionObject[] | null,
    brands: Record<string, Brand> | Brand[] | null = null,
  ): boolean {
    const identityBrands = brands ?? identity.value?.brands ?? {};
    if (permissions) {
      return Object.values(identityBrands).some((brand) =>
        permissions.every((permissionObject) => {
          const module =
            permissionObject.module?.value?.toLowerCase() ??
            (permissionObject.module as unknown as string)?.toLowerCase() ??
            '';
          const permission = permissionObject?.value?.toLowerCase();
          return !!brand?.permissions?.[module]?.[permission as string];
        }),
      );
    }
    return true;
  }

  function checkIfOrgPermissionsExist(
    permissions: PermissionObject[] | null,
    brands: Record<string, Brand> | Brand[],
  ): boolean {
    if (!permissions) return true;
    const organizationPermissions = identity.value?.organization_permissions;
    const userOrganizationPermissions = identity.value?.organization_user_permissions;

    return Object.values(brands).some((brand) =>
      permissions.every((permissionObject) => {
        const module = permissionObject?.module?.value?.toLowerCase();
        const permission = permissionObject?.value?.toLowerCase();
        if (!module || !permission || !brand.organizationId) return false;
        const orgPermission =
          !!organizationPermissions?.[brand.organizationId]?.[module]?.[permission];
        const userOrgPermission =
          !!userOrganizationPermissions?.[brand.organizationId]?.[module]?.[permission];
        return orgPermission && userOrgPermission;
      }),
    );
  }

  function brandIdInIdentityBrands(brandId: number): boolean {
    return identityBrandIds.value.includes(brandId);
  }

  function getActiveBrandsWithPermission(permissionObject: PermissionObject): Brand[] {
    return allActiveUserBrands.value.filter((brand) => guard(permissionObject, brand));
  }

  function getActiveBrandIdsWithPermission(permissionObject: PermissionObject): number[] {
    return getActiveBrandsWithPermission(permissionObject).map((brand) => brand.id);
  }

  function checkIfUserPermissionsExist(
    permissions: PermissionObject[] | null,
    brands: Record<string, Brand> | null = null,
  ): boolean {
    const identityBrands = brands || identity.value?.brands || {};
    if (permissions) {
      return Object.values(identityBrands).some((brand) =>
        permissions.every((permissionObject) => {
          const module = permissionObject?.module?.value?.toLowerCase();
          const permission = permissionObject?.value?.toLowerCase();
          if (!module || !permission) return false;
          return !!identity.value?.permissions?.[module]?.[brand.label]?.[permission];
        }),
      );
    }
    return false;
  }

  function filterBrands(search: string | null): Brand[] {
    const stringToMatch = search?.toLowerCase() ?? '';
    return Object.values(identity.value?.brands ?? {}).filter((brand) => {
      const matchesBrandName = brand?.name?.toLowerCase()?.includes(stringToMatch);
      const matchesBrandLabel = brand?.label?.toLowerCase()?.includes(stringToMatch);
      return matchesBrandName || matchesBrandLabel;
    });
  }

  function checkIfUserIsAdminForBrand(brandId: number): boolean {
    const brandRoles = identity.value?.permissions?.brand_roles ?? [];
    const selectedBrandRole = brandRoles.find((brandRole) => brandRole.brand_id === brandId);
    return selectedBrandRole?.role === 'admin';
  }

  return {
    // State
    allUserBrands,
    identity,
    subscriptions,
    actionMetadata,
    currentBrandLabel,

    // Computed - Identity
    identityBrandsById,
    isSuperAdmin,
    isShared,
    isDashSocialUser,

    // Computed - Brand
    currentBrand,
    currentBrandSubscriptions,
    currentOrganizationSubscriptions,
    currentSubscriptions,

    // Computed - Feature Access
    canAccessOrganic,
    canAccessVision,
    canAccessStoriesSwipeups,
    brandCanAccessScheduler,
    canAccessSchedulerPlatform,
    canAccessAutoPublish,
    canAccessSendNotification,
    isSchedulerPlatformUsable,
    brandCanAccessIndustryBenchmarks,

    // Computed - Permission Checkers
    brand_can,
    user_can,
    hasBoardsAccess,

    // Computed - Brand Roles
    currentBrandRole,
    currentPlanType,
    isCurrentBrandRoleAdmin,
    identityBrandIds,
    canOnlyAccessInactiveBrand,
    canAccessMultipleBrands,
    allActiveUserBrands,

    // Functions - Permission Guards
    guard,
    guardSome,

    // Functions - Brand Management
    setCurrentBrand,
    clearCurrentBrand,

    // Functions - Identity Management
    setIdentity,
    setSubscriptions,
    setActionMetadata,
    updateIdentity,
    clearIdentity,
    setCurrentBrandByAttributes,

    // Functions - Permission Checks
    checkIfBrandPermissionsExist,
    checkIfOrgPermissionsExist,
    checkIfUserPermissionsExist,
    filterBrands,
    brandIdInIdentityBrands,
    getActiveBrandsWithPermission,
    getActiveBrandIdsWithPermission,
    checkIfUserIsAdminForBrand,
  };
});
